/**
 * AdminPartnerPages.jsx
 *
 * Admin CRUD list + form for Partner CMS pages.
 *
 * MVP scope: list all pages, create new with key fields, edit JSON
 * for product cards / trust stats (advanced fields), publish/archive
 * status, link to public preview at /p/{slug}.
 *
 * Out of scope (future polish):
 *   - Drag-and-drop product cards
 *   - Live preview pane
 *   - Theme-color picker with swatches (currently free-text)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { Plus, ExternalLink, Edit3, Archive, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  slug: '', partner_name: '', partner_url: '', category_label: 'Partner',
  theme_color: 'amber',
  hero_title_line1: '', hero_title_line2: '', hero_subtitle: '',
  primary_cta_label: 'Visit partner site',
  secondary_cta_label: '', secondary_cta_url: '',
  products_heading: 'Featured offerings', products_subheading: '',
  member_perk_heading: 'AADC pays for itself', member_perk_body: '',
  partner_note: '',
  trust_stats_json: '[]',     // JSON-string for now (MVP)
  products_json: '[]',
  coming_soon_json: '[]',
};

export default function AdminPartnerPages() {
  const { user, loading: authLoading } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // null = closed; {} = create; {slug, ...} = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const isAdmin = !!(user && (user.is_admin || user.is_sysadmin || user.role === 'admin'));

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/partner-pages/admin/all`, { headers: { Authorization: `Bearer ${token}` } });
      setPages(r.data || []);
    } catch (err) {
      toast.error('Could not load partner pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin]);

  if (authLoading) return null;
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing({});
  };

  const openEdit = (p) => {
    setForm({
      ...EMPTY_FORM,
      ...p,
      trust_stats_json: JSON.stringify(p.trust_stats || [], null, 2),
      products_json: JSON.stringify(p.products || [], null, 2),
      coming_soon_json: JSON.stringify(p.coming_soon || [], null, 2),
    });
    setEditing(p);
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const isEdit = editing && editing.slug;

      let trust_stats, products, coming_soon;
      try {
        trust_stats = JSON.parse(form.trust_stats_json || '[]');
        products = JSON.parse(form.products_json || '[]');
        coming_soon = JSON.parse(form.coming_soon_json || '[]');
      } catch (e) {
        toast.error('JSON parse error in trust stats / products / coming soon');
        setSaving(false);
        return;
      }

      const payload = {
        ...form,
        trust_stats,
        products,
        coming_soon,
      };
      // strip the json string fields before sending
      delete payload.trust_stats_json;
      delete payload.products_json;
      delete payload.coming_soon_json;

      const url = isEdit ? `${API}/partner-pages/${editing.slug}` : `${API}/partner-pages`;
      const method = isEdit ? 'patch' : 'post';
      await axios[method](url, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isEdit ? 'Saved' : 'Created');
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (slug, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/partner-pages/${slug}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status: ${status}`);
      await load();
    } catch (err) {
      toast.error('Status change failed');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-partner-pages">
      <SEO title="Admin — Partner Pages" url="/admin/partner-pages" noindex />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold" data-testid="apc-title">Partner Pages CMS</h1>
            <p className="text-stone-600 mt-1">
              Create and publish partner landing pages. Each page auto-spawns a Community subgroup.
            </p>
          </div>
          <Button onClick={openCreate} data-testid="apc-new-btn">
            <Plus className="w-4 h-4 mr-2" /> New page
          </Button>
        </div>

        {loading && <div className="text-stone-500">Loading…</div>}
        {!loading && pages.length === 0 && (
          <Card><CardContent className="p-10 text-center text-stone-500">No partner pages yet — create the first.</CardContent></Card>
        )}

        <div className="space-y-3">
          {pages.map((p) => (
            <Card key={p.id} data-testid={`apc-row-${p.slug}`}>
              <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{p.partner_name}</h3>
                    <Badge variant={p.status === 'published' ? 'default' : 'outline'}>{p.status}</Badge>
                  </div>
                  <div className="text-sm text-stone-500 mt-1">
                    /p/{p.slug} · {p.category_label} · theme: {p.theme_color}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/p/${p.slug}`} target="_blank"><Button size="sm" variant="outline"><ExternalLink className="w-4 h-4" /></Button></Link>
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`apc-edit-${p.slug}`}><Edit3 className="w-4 h-4 mr-1" /> Edit</Button>
                  {p.status !== 'published' && (
                    <Button size="sm" onClick={() => setStatus(p.slug, 'published')} data-testid={`apc-publish-${p.slug}`}>Publish</Button>
                  )}
                  {p.status === 'published' && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(p.slug, 'draft')}>Unpublish</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setStatus(p.slug, 'archived')}><Archive className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Edit / Create modal */}
      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="apc-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.slug ? `Edit: ${editing.partner_name}` : 'New partner page'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Slug *</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="bringfido" disabled={!!editing?.slug} data-testid="apc-slug" />
              </div>
              <div>
                <Label>Partner name *</Label>
                <Input value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} data-testid="apc-partner-name" />
              </div>
              <div>
                <Label>Partner URL *</Label>
                <Input value={form.partner_url} onChange={(e) => setForm({ ...form, partner_url: e.target.value })} placeholder="https://" data-testid="apc-partner-url" />
              </div>
              <div>
                <Label>Category label</Label>
                <Input value={form.category_label} onChange={(e) => setForm({ ...form, category_label: e.target.value })} placeholder="Travel partner" />
              </div>
              <div>
                <Label>Theme color</Label>
                <Select value={form.theme_color} onValueChange={(v) => setForm({ ...form, theme_color: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['amber', 'emerald', 'sky', 'rose', 'indigo', 'stone'].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Primary CTA label</Label>
                <Input value={form.primary_cta_label} onChange={(e) => setForm({ ...form, primary_cta_label: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Hero title — line 1</Label>
              <Input value={form.hero_title_line1} onChange={(e) => setForm({ ...form, hero_title_line1: e.target.value })} />
            </div>
            <div>
              <Label>Hero title — line 2</Label>
              <Input value={form.hero_title_line2} onChange={(e) => setForm({ ...form, hero_title_line2: e.target.value })} />
            </div>
            <div>
              <Label>Hero subtitle</Label>
              <Textarea rows={2} value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} />
            </div>
            <div>
              <Label>Trust stats (JSON array of {`{value, label}`})</Label>
              <Textarea rows={3} value={form.trust_stats_json} onChange={(e) => setForm({ ...form, trust_stats_json: e.target.value })} className="font-mono text-xs" />
            </div>
            <div>
              <Label>Products (JSON array of product cards)</Label>
              <Textarea rows={6} value={form.products_json} onChange={(e) => setForm({ ...form, products_json: e.target.value })} className="font-mono text-xs" placeholder='[{"id":"hotel-1","name":"Sample Hotel","cta_url":"https://...","cta_label":"Book"}]' />
            </div>
            <div>
              <Label>Coming Soon (JSON array)</Label>
              <Textarea rows={3} value={form.coming_soon_json} onChange={(e) => setForm({ ...form, coming_soon_json: e.target.value })} className="font-mono text-xs" />
            </div>
            <div>
              <Label>Member perk body</Label>
              <Textarea rows={2} value={form.member_perk_body || ''} onChange={(e) => setForm({ ...form, member_perk_body: e.target.value })} />
            </div>
            <div>
              <Label>Partner note (admin-only)</Label>
              <Textarea rows={2} value={form.partner_note || ''} onChange={(e) => setForm({ ...form, partner_note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="apc-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
